import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { readStagedSuggestions, writeStagedSuggestions } from './stagedSuggestions'

/**
 * Shared engine for the astrologer's "suggest products" tools — used by the
 * appointment page's full panel and the video console's compact sidebar card,
 * so both stay on the exact same verified upstream contracts:
 *   search  GET  /product/search        (all filters optional)
 *   list    POST /astrologer-suggested-products  { astrologer_id, booking_id }
 *   add     POST /astrologer/add-to-cart { product_id, quantity, astrologer_id, user_id, booking_id }
 *   remove  POST /astrologer/remove-suggested-product { cart_id, astrologer_id, booking_id }
 */

export const EMPTY_FILTERS = {
  q: '',
  category_id: '',
  product_grade_id: '',
  ratti: '',
  carat: '',
  min_price: '',
  max_price: '',
  in_stock: false,
}

/** Form state → the exact query the upstream validates. Blanks are dropped;
 * in_stock only rides along when toggled (0 is not "no filter" upstream). */
function toSearchParams(filters, page, perPage) {
  const params = { per_page: perPage, page }
  if (filters.q.trim()) params.q = filters.q.trim()
  if (filters.category_id) params.category_id = Number(filters.category_id)
  if (filters.product_grade_id) params.product_grade_id = Number(filters.product_grade_id)
  if (filters.ratti !== '') params.ratti = Number(filters.ratti)
  if (filters.carat !== '') params.carat = Number(filters.carat)
  if (filters.min_price !== '') params.min_price = Number(filters.min_price)
  if (filters.max_price !== '') params.max_price = Number(filters.max_price)
  if (filters.in_stock) params.in_stock = 1
  return params
}

/** The search endpoint is a Laravel resource paginator (data + meta), but be
 * tolerant of envelope variants the way the legacy portal was. */
function unwrapSearch(res) {
  const payload = res?.data && !Array.isArray(res.data) ? res.data : res
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(res?.data)
      ? res.data
      : Array.isArray(payload)
        ? payload
        : []
  const meta = payload?.meta ?? res?.meta ?? {}
  return {
    items,
    total: meta.total ?? items.length,
    currentPage: meta.current_page ?? 1,
    lastPage: meta.last_page ?? 1,
  }
}

/** Suggestion rows arrive under data/items/carts (shape varies upstream);
 * only rows belonging to this booking count. Also exported for the seeker's
 * read-only view, which unwraps the same rows (list endpoint or cart rows —
 * suggestions ARE cart rows, so both shapes funnel through here). */
export function unwrapSuggestions(res, bookingId) {
  const payload = res?.data ?? res
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.carts)
        ? payload.carts
        : Array.isArray(payload)
          ? payload
          : []
  return items
    .filter((row) => row && String(row.booking_id ?? row.booking?.id) === String(bookingId))
    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
}

/** Discounted unit price of a suggestion row, mirroring the legacy math:
 * product_price minus discount_rate%, falling back through price fields. */
export function suggestionPrice(row) {
  const p = row?.product ?? {}
  const base = Number(p.product_price ?? p.price)
  const rate = Number(p.discount_rate)
  if (Number.isFinite(base) && Number.isFinite(rate) && rate > 0) {
    return base - (base * rate) / 100
  }
  if (Number.isFinite(base)) return base
  const fallback = Number(p.final_price ?? row?.price ?? row?.amount)
  return Number.isFinite(fallback) ? fallback : null
}

const apiMessage = (err, fallback) => err?.error?.message || err?.message || fallback

/** A staged (not yet sent) pick, shaped like a suggestion row so the same
 *  tiles render it. The `staged-` id prefix is what tells the two apart.
 *  The product is trimmed to what the tiles and suggestionPrice() read —
 *  staged rows are persisted to localStorage, and catalog records carry
 *  descriptions and metadata that have no business sitting there. */
function stagedRow(product, quantity) {
  return {
    id: `staged-${product.id}`,
    staged: true,
    product_id: Number(product.id),
    quantity: Number(quantity) || 1,
    product: {
      id: product.id,
      name: product.name,
      image: product.image ?? null,
      image_url: product.image_url ?? null,
      price: product.price,
      product_price: product.product_price,
      discount_rate: product.discount_rate,
      final_price: product.final_price,
    },
  }
}

/**
 * @param {object} booking
 * @param {object} [options]
 * @param {number} [options.perPage]
 * @param {boolean} [options.deferred] Hold every add/removal locally and send
 *   them in one batch from `commit()`. Both astrologer surfaces use this: a
 *   suggestion is a cart row, so committing per click would drop products into
 *   the customer's cart while the consultation is still running. The batch
 *   goes out when the astrologer finalizes the note on the appointment page.
 *   Staged picks are persisted per booking, so they survive the trip out of
 *   the video room, a reload, and finalizing later from another tab.
 */
export function useSuggestProducts(booking, { perPage = 8, deferred = false } = {}) {
  const queryClient = useQueryClient()
  const bookingId = booking.id
  // Same resolution order as the legacy portal's controller.
  const astrologerId = booking.astrologer_id ?? booking.astrologer?.id
  const customerId = booking.user_id ?? booking.user?.id

  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [submitted, setSubmitted] = useState(null) // filters snapshot; null until first search
  const [page, setPage] = useState(1)
  const [error, setError] = useState(null)

  /* ── Reference lists (small, cache hard) ── */
  const { data: categoriesRes } = useQuery({
    queryKey: ['product-categories'],
    queryFn: () => api.catalog.categories(),
    staleTime: 30 * 60_000,
  })
  const { data: gradesRes } = useQuery({
    queryKey: ['product-grades'],
    queryFn: () => api.catalog.productGrades(),
    staleTime: 30 * 60_000,
  })
  const categories = Array.isArray(categoriesRes?.data) ? categoriesRes.data : []
  const grades = Array.isArray(gradesRes?.data) ? gradesRes.data : Array.isArray(gradesRes) ? gradesRes : []

  /* ── Suggestions already pinned to this consultation ── */
  const suggestionsQuery = useQuery({
    queryKey: ['suggested-products', bookingId],
    queryFn: () => api.suggestedProducts.list({ astrologer_id: astrologerId, booking_id: bookingId }),
    enabled: Boolean(astrologerId && bookingId),
  })
  const committedSuggestions = useMemo(
    () => unwrapSuggestions(suggestionsQuery.data, bookingId),
    [suggestionsQuery.data, bookingId],
  )

  /* ── Deferred staging ──
     Picks wait here instead of going upstream: `staged` holds new ones,
     `removals` holds committed rows the astrologer took back. Both are applied
     by commit() and both are reflected in the list below, so the panel looks
     the same either way — only the timing of the writes changes. Seeded from
     (and mirrored to) localStorage so a pick made in the video room is still
     staged when the astrologer reaches the appointment page to finalize. */
  const [pending, setPending] = useState(() =>
    deferred ? readStagedSuggestions(bookingId) : { staged: [], removals: [] },
  )
  const [committing, setCommitting] = useState(false)
  const { staged, removals: pendingRemovals } = pending

  useEffect(() => {
    if (!deferred) return
    writeStagedSuggestions(bookingId, pending)
  }, [deferred, bookingId, pending])

  const suggestions = useMemo(() => {
    if (!deferred) return committedSuggestions
    return [...staged, ...committedSuggestions.filter((row) => !pendingRemovals.includes(row.id))]
  }, [deferred, committedSuggestions, staged, pendingRemovals])

  const suggestedProductIds = useMemo(
    () => new Set(suggestions.map((row) => Number(row.product_id ?? row.product?.id)).filter(Boolean)),
    [suggestions],
  )

  /* ── Catalog search — runs only after an explicit submit ── */
  const searchQuery = useQuery({
    queryKey: ['suggest-product-search', bookingId, submitted, page, perPage],
    queryFn: () => api.catalog.search(toSearchParams(submitted, page, perPage)),
    enabled: submitted !== null,
    placeholderData: (prev) => prev, // keep the list while paginating
  })
  const results = submitted !== null ? unwrapSearch(searchQuery.data) : null
  const searchState =
    submitted === null
      ? 'idle'
      : searchQuery.isPending
        ? 'loading'
        : searchQuery.isError
          ? 'error'
          : 'ready'

  const refreshSuggestions = () =>
    queryClient.invalidateQueries({ queryKey: ['suggested-products', bookingId] })

  const addMutation = useMutation({
    mutationFn: (payload) => api.suggestedProducts.add(payload),
    onSuccess: () => {
      setError(null)
      refreshSuggestions()
    },
    onError: (err) => setError(apiMessage(err, 'Could not suggest this product.')),
  })

  const removeMutation = useMutation({
    mutationFn: (cartId) =>
      api.suggestedProducts.remove({ cart_id: cartId, astrologer_id: astrologerId, booking_id: bookingId }),
    onSuccess: () => {
      setError(null)
      refreshSuggestions()
    },
    onError: (err) => setError(apiMessage(err, 'Could not remove this suggestion.')),
  })

  /**
   * Send everything staged, once. Upstream has no bulk route, so this is one
   * request per row — awaited in sequence (oldest pick first) so a failure
   * part-way leaves a predictable state. Rows that fail stay staged rather
   * than vanishing, and the counts are returned for the caller to report.
   * @returns {Promise<{committed: number, failed: number, added: number, removed: number}>}
   */
  const commit = async () => {
    if (!deferred || (!staged.length && !pendingRemovals.length)) {
      return { committed: 0, failed: 0, added: 0, removed: 0 }
    }
    setCommitting(true)
    let added = 0
    let removed = 0
    const failedRemovals = []
    const failedRows = []
    try {
      for (const cartId of pendingRemovals) {
        try {
          await api.suggestedProducts.remove({ cart_id: cartId, astrologer_id: astrologerId, booking_id: bookingId })
          removed += 1
        } catch {
          failedRemovals.push(cartId)
        }
      }
      for (const row of [...staged].reverse()) {
        try {
          await api.suggestedProducts.add({
            product_id: row.product_id,
            quantity: row.quantity,
            astrologer_id: astrologerId,
            user_id: customerId,
            booking_id: bookingId,
          })
          added += 1
        } catch {
          failedRows.push(row)
        }
      }
      // Whatever failed stays staged (and stays persisted) so the astrologer
      // can retry; a clean run empties the store.
      setPending({ staged: failedRows, removals: failedRemovals })
      await refreshSuggestions()
      const failed = failedRows.length + failedRemovals.length
      setError(
        failed
          ? `${failed} change${failed === 1 ? '' : 's'} could not be shared with the customer — please try again.`
          : null,
      )
      return { committed: added + removed, failed, added, removed }
    } finally {
      setCommitting(false)
    }
  }

  const submitSearch = (e) => {
    e?.preventDefault()
    setPage(1)
    setSubmitted({ ...filters })
  }

  const clearFilters = () => setFilters((f) => ({ ...EMPTY_FILTERS, q: f.q }))

  const setField = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  const activeFilterCount =
    ['category_id', 'product_grade_id', 'ratti', 'carat', 'min_price', 'max_price']
      .filter((k) => filters[k] !== '').length + (filters.in_stock ? 1 : 0)

  return {
    // filters
    filters,
    setField,
    clearFilters,
    activeFilterCount,
    submitSearch,
    // search results
    searchState,
    results,
    fetching: searchQuery.isFetching,
    searchError: searchQuery.isError
      ? apiMessage(searchQuery.error, 'Product search failed — please try again.')
      : null,
    page,
    setPage,
    // reference lists
    categories,
    grades,
    // suggestions
    suggestions,
    suggestionsLoading: suggestionsQuery.isPending,
    suggestedProductIds,
    // mutations
    suggest: (product, quantity) => {
      if (deferred) {
        setError(null)
        setPending((p) =>
          p.staged.some((r) => r.product_id === Number(product.id))
            ? p
            : { ...p, staged: [stagedRow(product, quantity), ...p.staged] },
        )
        return
      }
      addMutation.mutate({
        product_id: product.id,
        quantity,
        astrologer_id: astrologerId,
        user_id: customerId,
        booking_id: bookingId,
      })
    },
    addingId: addMutation.isPending ? addMutation.variables?.product_id : null,
    removeSuggestion: (cartId) => {
      if (deferred) {
        setError(null)
        // A staged pick was never sent, so it just disappears; a committed row
        // has to be queued and removed upstream when the batch goes out.
        if (String(cartId).startsWith('staged-')) {
          setPending((p) => ({ ...p, staged: p.staged.filter((r) => r.id !== cartId) }))
        } else {
          setPending((p) => (p.removals.includes(cartId) ? p : { ...p, removals: [...p.removals, cartId] }))
        }
        return
      }
      removeMutation.mutate(cartId)
    },
    removingId: removeMutation.isPending ? removeMutation.variables : null,
    // deferred mode
    commit,
    committing,
    pendingCount: staged.length + pendingRemovals.length,
    error,
  }
}
